import React from 'react';
import { Group, Input, Button, Tabs, Stack, Box, Select } from '@mantine/core';
import { Icon } from '@iconify/react';

import { Icons } from 'src/components/ui/Icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ViewModeToggle } from 'src/components/ui/ViewModeToggle';
import classes from './PageFilters.module.css'

type ClickFilterConfig<T> = {
    label: string;
    filter: T;
}

export type ClickFilterSetConfig<T> = {
    field: string;
    label: string;
    values: { [key: string]: ClickFilterConfig<T> };
    default?: string;
    disableAll?: boolean;
    placeholder?: string;
    searchable?: boolean;
}

type ClickFilterSetRenderData = {
    field: string;
    label: string;
    placeholder: string;
    searchable: boolean;
    disableAll?: boolean;
    value: string;
    items: { label: string; value: string }[];
    onChange: (value: string | null) => void;
}

export const createClickFilterEnumValues = <
    TEnum extends string,
    TBoolExp extends Record<string, any> = any
>(
    enumObj: Record<string, TEnum>,
    fieldName: string = 'type'
) => {
    return (Object.values(enumObj) as string[])
        .map(i => i.toLowerCase())
        .reduce((obj, key) => {
            obj[key] = {
                label: key.charAt(0).toUpperCase() + key.slice(1),
                filter: [{ [fieldName]: { _eq: key.toLocaleLowerCase() as TEnum } }] as any
            }
            return obj
        }, {} as { [key: string]: ClickFilterConfig<TBoolExp['_and']> })
}

export type useFiltersProps<
    FilterType extends FilterTypeBase = null,
    ItemType extends Record<string, any> = {},
    RelationTypes extends Record<string, any> = {}
> = {
    baseFilters: FilterType;
    clickFilters: FilterType;
    searchFilterFields: (keyof ItemType)[];
    searchFilterRelations?: {
        [K in keyof RelationTypes]: (keyof RelationTypes[K])[]
    };
    clickFilterField: string;
    clickFilterValues: {
        [index: string]: ClickFilterConfig<FilterType>;
    },
    clickFilterDefault?: string;
    clickFiltersDisableDefaultAll?: boolean;
    clickFilterSets?: ClickFilterSetConfig<FilterType>[];
    overlayMode?: boolean;
}

// type FilterVariablesType = {
//     where: {
//         _and: any[]
//     }
// }

type ClickFilterType = {
    value: string;
}

type FilterTypeBase = any[] | undefined | null

export function usePageFilters<
    FilterType extends FilterTypeBase = null,
    ItemType extends Record<string, any> = {},
    RelationTypes extends Record<string, any> = {}
>(props: useFiltersProps<FilterType, ItemType, RelationTypes>) {

    let [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const location = useLocation();

    const [baseFilters] = React.useState<FilterType>(props.baseFilters)
    const [clickFilters, setClickFilters] = React.useState<FilterType>(props.clickFilters)
    const [searchFilters, setSearchFilters] = React.useState<FilterType>([] as unknown as FilterType)

    const [clickFilter, setClickFilter] = React.useState<ClickFilterType>(props.clickFilterDefault ? { value: props.clickFilterDefault } : { value: 'all' });
    const [searchFilter, setSearchFilter] = React.useState<string>('');
    const [searchFilterActive, setSearchFilterActive] = React.useState<boolean>(false);

    const [clickFilterSetSelections, setClickFilterSetSelections] = React.useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        (props.clickFilterSets ?? []).forEach(set => {
            initial[set.field] = set.default ?? 'all';
        });
        return initial;
    });

    const queryParamFiltersApplied = React.useRef(false)

    const clickFilterSetSelectionsRef = React.useRef(clickFilterSetSelections);
    clickFilterSetSelectionsRef.current = clickFilterSetSelections;

    const clickFilterSetsRef = React.useRef(props.clickFilterSets);
    clickFilterSetsRef.current = props.clickFilterSets;

    const locationRef = React.useRef(location);
    locationRef.current = location;

    // const [filters, setFilters] = React.useState<FilterVariablesType>({
    //     where: {
    //         _and: [
    //             ...(baseFilters as FilterTypeBase || []),
    //             ...(clickFilters as FilterTypeBase || []),
    //             ...(searchFilters as FilterTypeBase || [])
    //         ]
    //     }
    // })

    const clickFilterSetFilters = React.useMemo<FilterType>(() => {
        const sets = clickFilterSetsRef.current;
        if (!sets?.length) return [] as unknown as FilterType;
        const filters: any[] = [];
        sets.forEach(set => {
            const selectedKey = clickFilterSetSelections[set.field];
            if (selectedKey && selectedKey !== 'all' && set.values[selectedKey]) {
                filters.push(...(set.values[selectedKey].filter as FilterTypeBase || []));
            }
        });
        return filters as FilterType;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clickFilterSetSelections]);

    const newFilters = React.useMemo(() => {
        let renderedFilters: { [index: string]: any } = {
            where: {
                _and: [
                    ...(baseFilters as FilterTypeBase || []),
                    ...(clickFilters as FilterTypeBase || []),
                    ...(clickFilterSetFilters as FilterTypeBase || [])
                ]
            }
        }
        if (searchFilters?.length) {
            renderedFilters.where._or = [...(searchFilters as FilterTypeBase || [])]
        }
        return renderedFilters
    }, [searchFilters, baseFilters, clickFilters, clickFilterSetFilters])

    // React.useEffect(() => {
    //     setFilters({
    //         where: {
    //             _and: [
    //                 ...(baseFilters as FilterTypeBase || []),
    //                 ...(clickFilters as FilterTypeBase || []),
    //                 ...(searchFilters as FilterTypeBase || [])
    //             ]
    //         }
    //     })
    // }, [newFilters])

    const handleClickFilterChanged = React.useCallback((value: string) => {
        setClickFilter({ value });
        if (!props.clickFiltersDisableDefaultAll && value === 'all') {
            setClickFilters(props.clickFilters)
        } else {
            setClickFilters(props.clickFilterValues[value].filter)
        }
    }, [props.clickFilters, props.clickFilterValues, props.clickFiltersDisableDefaultAll])


    React.useEffect(() => {
        if (!queryParamFiltersApplied.current) {
            for (var searchParamKey of searchParams.keys()) {
                if (props.clickFilterValues && props.clickFilterField === searchParamKey) {
                    let searchParamValue = searchParams.get(searchParamKey)
                    if (searchParamValue) {
                        let clickFilterIndex = Object.keys(props.clickFilterValues).indexOf(searchParamValue)
                        if (clickFilterIndex >= 0) {
                            handleClickFilterChanged(searchParamValue)
                        }
                    }
                }
            }

            if (props.clickFilterSets) {
                props.clickFilterSets.forEach(set => {
                    const paramValue = searchParams.get(set.field);
                    if (paramValue && (paramValue === 'all' || set.values[paramValue])) {
                        setClickFilterSetSelections(prev => ({ ...prev, [set.field]: paramValue }));
                    }
                });
            }

            queryParamFiltersApplied.current = true;
        }
    }, [searchParams, handleClickFilterChanged, props.clickFilterValues, props.clickFilterField, props.clickFilterSets])

    const onSearchFilterChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchFilterActive(false)
        setSearchFilter(e.target.value)
    }

    const onSearchFilterGo = () => {

        if (searchFilter.length > 2 && (props.searchFilterFields.length || (props.searchFilterRelations && Object.keys(props.searchFilterRelations).length))) {

            setSearchFilterActive(true)

            let finalFilters = []

            let basicFilters = {
                _or: props.searchFilterFields.map(field => {
                    let ourField = field as string
                    let filterOperator = ourField.includes('_number') ? '_eq' : '_ilike'
                    let filterValue = ourField.includes('_number') ? searchFilter : `%${searchFilter}%`
                    return { [field]: { [filterOperator]: filterValue } }
                })
            }

            if (props.searchFilterFields.length) {
                finalFilters.push(basicFilters)
            }

            let relationFilters = {
                _or: props.searchFilterRelations ? Object.keys(props.searchFilterRelations).reduce((currentFilters, relationKey) => {
                    if (props.searchFilterRelations) {
                        let relationFields = props.searchFilterRelations[relationKey]
                        return [
                            ...currentFilters,
                            {
                                [relationKey]: {
                                    _or: relationFields.map(relationField => {
                                        let ourField = relationField as string
                                        let filterOperator = ourField.includes('_number') ? '_eq' : '_ilike'
                                        let filterValue = ourField.includes('_number') ? searchFilter : `%${searchFilter}%`
                                        return { [relationField]: { [filterOperator]: filterValue } }
                                    })
                                }
                            }
                        ]
                    }
                    return currentFilters
                }, [] as any[]) : []
            }

            if (props.searchFilterRelations && Object.keys(props.searchFilterRelations).length) {
                finalFilters.push(relationFilters)
            }

            setSearchFilters(finalFilters as FilterType)

        }
    }

    const onSearchFilterClear = () => {
        setSearchFilter('')
        setSearchFilterActive(false)
        setSearchFilters([] as unknown as FilterType)
    }

    const buildSetSearchParams = React.useCallback((overrides?: Record<string, string>) => {
        const params = new URLSearchParams();
        const selections = { ...clickFilterSetSelectionsRef.current, ...overrides };
        Object.entries(selections).forEach(([field, value]) => {
            if (value && value !== 'all') params.set(field, value);
        });
        return params;
    }, []);

    const onClickFilterChanged = (value: string) => {
        handleClickFilterChanged(value)
        const params = buildSetSearchParams();
        params.set(props.clickFilterField, value);
        navigate(
            {
                pathname: locationRef.current.pathname,
                search: params.toString(),
            },
            {
                state: locationRef.current.state, replace: true
            }
        )
    };

    const onClickFilterSetChanged = React.useCallback((field: string, value: string | null) => {
        const resolvedValue = value ?? 'all';
        setClickFilterSetSelections(prev => ({ ...prev, [field]: resolvedValue }));
        const params = buildSetSearchParams({ [field]: resolvedValue });
        if (clickFilter.value !== 'all') {
            params.set(props.clickFilterField, clickFilter.value);
        }
        navigate(
            {
                pathname: locationRef.current.pathname,
                search: params.toString(),
            },
            {
                state: locationRef.current.state, replace: true
            }
        );
    }, [buildSetSearchParams, clickFilter.value, props.clickFilterField, navigate]);

    const onSearchFilterKeyUp = (event: React.KeyboardEvent) => {
        if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            onSearchFilterGo()
        }
    }

    const clickFilterItems = Object.keys(props.clickFilterValues).map(i => {
        let item = props.clickFilterValues[i];
        return {
            label: item.label,
            value: i
        }
    })

    const clickFilterSetsRenderData: ClickFilterSetRenderData[] = React.useMemo(() =>
        (clickFilterSetsRef.current ?? []).map(set => ({
            field: set.field,
            label: set.label,
            placeholder: set.placeholder ?? set.label,
            searchable: set.searchable ?? true,
            disableAll: set.disableAll,
            value: clickFilterSetSelections[set.field] ?? 'all',
            items: [
                ...(set.disableAll ? [] : [{ label: 'All', value: 'all' }]),
                ...Object.entries(set.values).map(([key, config]) => ({
                    label: config.label,
                    value: key
                }))
            ],
            onChange: (value: string | null) => onClickFilterSetChanged(set.field, value)
        })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clickFilterSetSelections, onClickFilterSetChanged]
    );

    return {
        filters: newFilters,
        filterParams: {
            searchFilter,
            onSearchFilterChanged,
            onSearchFilterGo,
            onSearchFilterClear,
            onSearchFilterKeyUp,
            searchFilterActive,
            clickFilter,
            onClickFilterChanged,
            clickFilterItems,
            clickFiltersDisableDefaultAll: props.clickFiltersDisableDefaultAll,
            clickFilterSets: clickFilterSetsRenderData
        }
    }

}

type FilterComponentProps = {
    options: {
        searchFilter: string;
        searchFilterActive: boolean;
        clickFilter: ClickFilterType;
        clickFilterItems: { label: string; value: string; }[];
        clickFiltersDisableDefaultAll?: boolean;
        clickFilterSets?: ClickFilterSetRenderData[];
        onSearchFilterChanged(e: React.ChangeEvent<HTMLInputElement>): void;
        onSearchFilterGo(): void;
        onSearchFilterClear(): void;
        onSearchFilterKeyUp(event: React.KeyboardEvent): void;
        onClickFilterChanged(value: string | null): void;
    }
    extras?: React.ReactElement
}

export const PageFilters: React.FC<FilterComponentProps> = ({ options, extras }) => {

    const renderClickFilterSets = () => {
        if (!options.clickFilterSets?.length) return null;
        return (
            <Group gap="xs">
                {options.clickFilterSets.map(set => (
                    <Select
                        key={set.field}
                        placeholder={set.placeholder}
                        searchable={set.searchable}
                        data={set.items}
                        value={set.value}
                        onChange={set.onChange}
                        allowDeselect={false}
                        classNames={{
                            input: classes.search_input,
                            section: classes.search_section
                        }}
                        w={175}
                    />
                ))}
            </Group>
        );
    };

    const renderClickFilter = (grow: boolean = false) => {
        return (
            <Tabs
                value={options.clickFilter.value}
                onChange={options.onClickFilterChanged}
                variant="unstyled"
                classNames={{
                    tab: classes.tab
                }}
            >
                <Tabs.List grow={grow}>
                    {options.clickFiltersDisableDefaultAll ? null : <Tabs.Tab value='all'>All</Tabs.Tab>}
                    {options.clickFilterItems.map((f, k) => <Tabs.Tab key={k} value={f.value} >{f.label}</Tabs.Tab>)}
                </Tabs.List>
            </Tabs>
        )
    }

    const renderViewFilters = () => {
        return (
            <React.Fragment>
                <ViewModeToggle />
            </React.Fragment>
        )
    }

    const renderSearchFilter = () => {
        return (
            <Input
                leftSection={<Icon icon={Icons.search} />}
                placeholder="Search..."
                classNames={{
                    input: classes.search_input,
                    section: classes.search_section
                }}
                rightSection={
                    <Button
                        size='xs'
                        variant="subtle"
                        color={options.searchFilter.length >= 2 ? options.searchFilterActive ? 'red' : 'blue' : 'gray'}
                        onClick={options.searchFilterActive ? options.onSearchFilterClear : options.onSearchFilterGo}
                        px={3}
                    >
                        <Icon icon={options.searchFilterActive ? Icons.cancel : Icons.go} width={24} />
                    </Button>
                }
                rightSectionPointerEvents="visible"
                onKeyUp={options.onSearchFilterKeyUp}
                disabled={options.searchFilterActive}
                value={options.searchFilter}
                onChange={options.onSearchFilterChanged}
            />
        )
    }

    const renderExtras = () => {
        if (!extras) return null
        return (
            <React.Fragment>
                {extras}
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <Box visibleFrom="sm">
                <Group justify="space-between" mb="md" align="center">
                    <Group gap="md">
                        {renderClickFilter()}
                        {renderClickFilterSets()}
                    </Group>
                    <Group gap={2}>
                        {renderExtras()}
                        {renderViewFilters()}
                        {renderSearchFilter()}
                    </Group>
                </Group>
            </Box>
            <Box hiddenFrom="sm">
                <Stack mb="md">
                    {renderClickFilter(true)}
                    {renderClickFilterSets()}
                    <Group grow justify="space-between">
                        {renderExtras()}
                        {renderSearchFilter()}
                    </Group>
                </Stack>
            </Box>
        </React.Fragment>
    )

}